<template>
  <div class="page-wrapper">
    <div class="card shadow-sm border-0">
      <div class="card-header bg-white d-flex justify-content-between align-items-center border-bottom">
        <h4 class="card-title mb-0">
          <i class="bi bi-bank me-2"></i>Manage Funding & Training Opportunities
        </h4>
        <button class="btn btn-primary btn-sm new-album-btn" @click="openModal">
          <i class="bi bi-plus-lg me-1"></i> Add Funding
        </button>
      </div>

      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped table-hover align-middle">
            <thead class="table-light">
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Status</th>
              <th>Link</th>
              <th>Created</th>
              <th class="text-center" style="width: 120px;">Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in fundings" :key="item.id">
              <td class="fw-medium">{{ item.title }}</td>
              <td>{{ item.category }}</td>
              <td>
                  <span
                    class="badge"
                    :class="item.status === 'active' ? 'bg-success' : 'bg-secondary'"
                  >
                    {{ item.status }}
                  </span>
              </td>
              <td>
                <a
                  v-if="item.link"
                  :href="item.link"
                  target="_blank"
                  class="text-decoration-none"
                >
                  {{ truncate(item.link, 30) }}
                </a>
              </td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td class="text-center">
                <div class="dropdown">
                  <button
                    class="btn btn-sm btn-outline-secondary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i class="bi bi-three-dots"></i>
                  </button>
                  <ul class="dropdown-menu">
                    <li><a class="dropdown-item" href="#" @click="editFunding(item)">Edit</a></li>
                    <li><hr class="dropdown-divider"></li>
                    <li><a class="dropdown-item text-danger" href="#" @click="deleteFunding(item.id)">Delete</a></li>
                  </ul>
                </div>
              </td>
            </tr>

            <tr v-if="!fundings.length">
              <td colspan="6" class="text-center text-muted py-3">
                No records found
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal fade show" style="display: block;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-semibold">
              {{ editMode ? 'Edit Opportunity' : 'Add New Opportunity' }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            ></button>
          </div>

          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-medium">Title</label>
              <input
                type="text"
                v-model="form.title"
                class="form-control"
                placeholder="Enter title"
              />
            </div>
            <div class="mb-3">
              <label class="form-label fw-medium">Category</label>
              <select v-model="form.category" class="form-select">
                <option disabled value="">Select category</option>
                <option value="Funding opportunities">Funding opportunities</option>
                <option value="Training opportunities">Training opportunities</option>
                <option value="Scholarship opportunities">Scholarship opportunities</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label fw-medium">Content</label>
              <textarea
                v-model="form.content"
                class="form-control"
                rows="4"
                placeholder="Enter description"
              ></textarea>
            </div>
            <div class="mb-3">
              <label class="form-label fw-medium">Link</label>
              <input
                type="url"
                v-model="form.link"
                class="form-control"
                placeholder="https://example.com"
              />
            </div>
            <div class="mb-3">
              <label class="form-label fw-medium">Status</label>
              <select v-model="form.status" class="form-select">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div class="modal-footer bg-light">
            <button class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button class="btn btn-primary" @click="saveFunding">
              <i class="bi" :class="editMode ? 'bi-save' : 'bi-check2-circle'"></i>
              {{ editMode ? 'Update' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay when modal is open -->
    <div v-if="showModal" class="modal-backdrop fade show"></div>

    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FundingService from '@/services/funding/FundingService'

export default {
  name: 'FundingTraining',
  data() {
    return {
      fundings: [],
      showModal: false,
      editMode: false,
      form: {
        id: null,
        title: '',
        category: '',
        content: '',
        link: '',
        status: 'inactive'
      },
      toast: {
        show: false,
        type: 'success',
        message: ''
      },
    }
  },
  async mounted() {
    await this.loadFundings()
  },
  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    async loadFundings() {
      this.fundings = await FundingService.getAll()
    },
    openModal() {
      this.editMode = false
      this.resetForm()
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    editFunding(item) {
      this.editMode = true
      this.form = { ...item }
      this.showModal = true
    },
    async saveFunding() {
      try {
        const payload = { ...this.form, category: this.form.category.trim() }

        if (this.editMode) {
          await FundingService.update(this.form.id, payload)
          this.showToast('success','Funding updated successfully.')
        } else {
          await FundingService.create(payload)
          this.showToast('success','Funding created successfully.')
        }

        this.closeModal()
        await this.loadFundings()
      } catch (error) {
        console.error('Error saving funding:', error)
        this.showToast('error','Something went wrong.')
      }
    },
    async deleteFunding(id) {
      if (!confirm('Are you sure you want to delete this record?')) return
      await FundingService.delete(id)
      this.showToast('success','Funding deleted.')
      await this.loadFundings()
    },
    truncate(text, length) {
      return text && text.length > length ? text.substring(0, length) + '...' : text
    },
    formatDate(date) {
      return new Date(date).toLocaleString()
    },
    resetForm() {
      this.form = {
        id: null,
        title: '',
        category: '',
        content: '',
        link: '',
        status: 'inactive'
      }
    }
  }
}
</script>

<style scoped>
.new-album-btn {
  background: #009CAD;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.new-album-btn:hover {
  background: #008a9a;
}

.page-wrapper {
  padding: 1rem 0;
}
.table thead th {
  font-weight: 600;
  color: #495057;
}
.table tbody td {
  vertical-align: middle;
}
.modal-content {
  border-radius: 8px;
}
</style>
